'use client';

import { FC } from 'react';
import { FlexibleHeroBlockSection } from '../FlexibleHeroBlockSection';
import { FlexibleHeroBlockProps } from '../types';
import {
  getAlignmentClasses,
  getTextClasses,
  normalizeButtons,
} from '../utils';
import { CampaignLayout } from './components/CampaignLayout';
import { ModernLayout } from './components/ModernLayout';
import { EnumFlexibleHeroBlockHeroType } from '$/(cms)/_modules/cmsService/types';
import { useClientContext } from '$/modules/context/client';

/**
 * The hero every block type renders with, and the Product hero for verticals
 * without a variant of their own (see `PRODUCT_HERO_VARIANTS`).
 */
export const FlexibleHeroBlockDefault: FC<FlexibleHeroBlockProps> = ({
  id,
  backgroundImageDesktop,
  backgroundImageMobile,
  bodyCopy,
  ctaPrimary,
  ctaSecondary,
  headline,
  highlights,
  heroType,
  imageDesktop,
  imageMobile,
  keyStats,
  overline,
  offers,
  product,
  provider,
  productCallToAction,
  providerCallToAction,
  rewardType,
  showBreadcrumbs,
  showTimestamp,
  theme,
  textAlign,
  ContentContainer,
  isPageHeadline = true,
  wideLayoutBreakpoint,
  ctaLinkOverride,
}) => {
  const { brand, lang } = useClientContext();

  const buttons = normalizeButtons(ctaPrimary, ctaSecondary);
  const textClasses = getTextClasses(theme);
  const alignment = getAlignmentClasses(textAlign);
  const headingId = headline ? `flexible-hero-${id}` : undefined;
  const heroVariant = heroType ?? EnumFlexibleHeroBlockHeroType.Campaign;
  const isCampaign = heroVariant === EnumFlexibleHeroBlockHeroType.Campaign;

  return (
    <FlexibleHeroBlockSection
      theme={theme}
      backgroundImageDesktop={backgroundImageDesktop}
      backgroundImageMobile={backgroundImageMobile}
      ContentContainer={ContentContainer}
      headingId={headingId}
    >
      {isCampaign ? (
        <CampaignLayout
          alignment={alignment}
          buttons={buttons}
          bodyCopy={bodyCopy}
          headline={headline}
          headingId={headingId}
          isPageHeadline={isPageHeadline}
          imageDesktop={imageDesktop}
          imageMobile={imageMobile}
          lang={lang}
          offers={offers}
          overline={overline}
          rewardType={rewardType}
          textClasses={textClasses}
          theme={theme}
        />
      ) : (
        <ModernLayout
          alignment={alignment}
          buttons={buttons}
          bodyCopy={bodyCopy}
          brand={brand}
          headline={headline}
          headingId={headingId}
          isPageHeadline={isPageHeadline}
          highlights={highlights}
          heroVariant={heroVariant}
          imageDesktop={imageDesktop}
          imageMobile={imageMobile}
          keyStats={keyStats}
          lang={lang}
          overline={overline}
          product={product}
          productCallToAction={productCallToAction}
          provider={provider}
          providerCallToAction={providerCallToAction}
          showBreadcrumbs={showBreadcrumbs}
          showTimestamp={showTimestamp}
          textClasses={textClasses}
          theme={theme}
          wideLayoutBreakpoint={wideLayoutBreakpoint}
          ctaLinkOverride={ctaLinkOverride}
        />
      )}
    </FlexibleHeroBlockSection>
  );
};
